import { FC, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { IColumnToSave, IPage } from "../default/defaultGrid";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { Tooltip } from "antd";
import classNames from "classnames";
import { MultiplyIcon } from "../../../libs/corporate/icons/outlined/suggested/SymbolCollection";
import { DateInput, TextInput } from "../../controls/inputs";
import BaseIconButton from "../../buttons/iconButtons/BaseIconButton";
import { DecimalInputV2 } from "../../controls/inputs/BaseInput";
import { CheckBox } from "../../controls/checkbox";
import styles from './TableHeader.module.scss'
import { debounce } from "lodash";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DELAY = 1250
interface IClsProps {
    tD: string;
    tooltip: string;
    sortingDiv: string;
    sortingDivButton: string;
    searchDropDivButton?: string;
    filteredDiv: string;
}

interface ITableHeaderProps {
    index: number;
    draggable: boolean;
    item: IGridColumn;
    filtered: boolean;
    pluginSettings: IPluginSettings;
    setGridColumnsToSave: (value: SetStateAction<IColumnToSave>) => void;
    sorting: boolean;
    onSort: (column: IGridColumn) => void;
    searching?: boolean;
    onSearch?: (value: string, column: IGridColumn,) => void;
    renderSortIcon: (item: IGridColumn) => JSX.Element;
    onFilterDelete: (column: IGridColumn) => void;
    onPageNumberChange?: (pageNumber: number) => void;
    setPageNumber?: (value: SetStateAction<IPage>) => void;
    setLoadingStatus?: ((value: LoadingStatus) => void) | undefined;
    filter?: IGridFilter;
    cls: IClsProps;
    value?: string
    onColumnReorder?: (columns: IGridColumn[]) => void
    onResizeStart?: () => void;
    onReorderEnd?: () => void;
}

export const TableHeader: FC<ITableHeaderProps> = ({
    index,
    draggable,
    item,
    filtered,
    pluginSettings,
    setGridColumnsToSave,
    sorting,
    onSort,
    searching,
    onSearch,
    renderSortIcon,
    onFilterDelete,
    onPageNumberChange,
    setPageNumber,
    setLoadingStatus,
    filter,
    cls,
    value,
    onResizeStart,
    onReorderEnd,
}) => {
    const [spanWidth, setSpanWidth] = useState<boolean>(false);
    const [showSearchInput, setShowSearchInput] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>(value ?? '');
    const [boolean, setBoolean] = useState<boolean>();
    const TableHeaderRef = useRef<HTMLTableCellElement>(null);

    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [resizeStartX, setResizeStartX] = useState<number>(0);
    const [resizeStartWidth, setResizeStartWidth] = useState<number>(item.width || 100);
    const [currentWidth, setCurrentWidth] = useState<number>(item.width || 100);

    useEffect(() => {
        setCurrentWidth(item.width || 100);
    }, [item.width]);
    const [isHoveringResize, setIsHoveringResize] = useState<boolean>(false);

    const debouncedSearch = useRef(
        debounce((value: string, column: IGridColumn) => {
            if (value.length >= MIN_SEARCH_LENGTH || value.length === 0) {
                onSearch?.(value, column);
            }
        }, SEARCH_DELAY)
    ).current;

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearchChange = (value: string) => {
        setSearchText(value);
        
        if (value.length === 0) {
            debouncedSearch.cancel();
            onSearch?.('', item);
        } else {
            debouncedSearch(value, item);
        }
    };

    const resizeActiveRef = useRef<boolean>(false)

    const handleCellMouseDown = (e: React.MouseEvent<HTMLTableCellElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const isOnBorder = e.clientX > rect.right - 8;
        
        if ((isOnBorder || isHoveringResize) && !showSearchInput) {
            e.preventDefault();
            e.stopPropagation();
            startResizing(e.clientX);
        }
    };

    const handleResizeHandleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        startResizing(e.clientX);
    };

    const startResizing = (clientX: number) => {
        if (showSearchInput) return;

        onResizeStart?.();

        setIsResizing(true);
        resizeActiveRef.current = true;
        setResizeStartX(clientX);
        setResizeStartWidth(item.width || 100);

        if (TableHeaderRef.current) {
            TableHeaderRef.current.dataset.originalWidth = String(item.width || 100);
        }
        
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100vw';
        overlay.style.height = '100vh';
        overlay.style.zIndex = '9999';
        overlay.style.cursor = 'col-resize';
        overlay.id = 'resize-overlay';
        document.body.appendChild(overlay);
    };

    const handleResizeMove = useCallback((e: MouseEvent) => {
        if (!resizeActiveRef.current || !TableHeaderRef.current) return;

        const diff = e.clientX - resizeStartX;
        const newWidth = Math.max(50, Math.max(100, resizeStartWidth + diff)); // Минимальная ширина 50px

        const table = TableHeaderRef.current.closest('table');
        if (!table) return;

        const columnOrder = TableHeaderRef.current.dataset.orderNumber;
        const propertyName = TableHeaderRef.current.dataset.propertyName;
        
        if (!columnOrder || !propertyName) return;

        TableHeaderRef.current.style.width = `${newWidth}px`;
        TableHeaderRef.current.style.minWidth = `${newWidth}px`;

        const allHeaderCells = table.querySelectorAll(`thead tr td[data-order-number="${columnOrder}"]`);
        allHeaderCells.forEach(cell => {
            (cell as HTMLElement).style.width = `${newWidth}px`;
            (cell as HTMLElement).style.minWidth = `${newWidth}px`;
        });

        const dataRows = table.querySelectorAll('tbody tr');
        dataRows.forEach(row => {
            const cells = Array.from(row.children) as HTMLTableCellElement[];
            const dataCell = cells.find(cell => {
                const cellPropertyName = cell.dataset.propertyName;
                return cellPropertyName === propertyName;
            });
            
            if (dataCell) {
                dataCell.style.width = `${newWidth}px`;
                dataCell.style.minWidth = `${newWidth}px`;
            }
        });
    }, [resizeStartX, resizeStartWidth]);

    const handleResizeEnd = useCallback(() => {
        if (!resizeActiveRef.current) return;

        resizeActiveRef.current = false;

        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        const overlay = document.getElementById('resize-overlay');
        if (overlay) overlay.remove();

        if (TableHeaderRef.current) {
            const newWidth = TableHeaderRef.current.offsetWidth;
            setCurrentWidth(newWidth);
            setIsResizing(false);

            const updatedColumns = pluginSettings.columns.map(col =>
                col.propertyName === item.propertyName
                    ? { ...col, width: newWidth }
                    : col
            );

            setGridColumnsToSave(prev => ({
                ...prev,
                columns: updatedColumns,
                save: true
            }));
        } else {
            setIsResizing(false);
        }
    }, [pluginSettings.columns, item.propertyName, setGridColumnsToSave]);

    useEffect(() => {
        if (!isResizing) return;
        
        const handleGlobalMouseMove = (e: MouseEvent) => {
            handleResizeMove(e);
        };
        
        const handleGlobalMouseUp = () => {
            handleResizeEnd();
        };
        
        document.addEventListener('mousemove', handleGlobalMouseMove);
        document.addEventListener('mouseup', handleGlobalMouseUp);
        
        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [isResizing, handleResizeMove, handleResizeEnd]);

    const handleResizeHandleMouseEnter = () => {
        setIsHoveringResize(true);
    };
    
    const handleResizeHandleMouseLeave = () => {
        setIsHoveringResize(false);
    };

    const actionForRef = (ref) => {
        if (ref && ref.current) {
            if(sorting) {
                const tdElement = ref.current;
                const tdElementCss = getComputedStyle(tdElement);
                const tdWidth = tdElement.offsetWidth - (parseFloat(tdElementCss.paddingLeft) + parseFloat(tdElementCss.paddingRight))
                
                const span = tdElement.querySelector('span');
                const spanWidth = span?.scrollWidth;
                
                const div = tdElement.querySelector('div');
                const divWidth = div?.scrollWidth;
    
                const spanWithDivWidth = spanWidth + divWidth;
                spanWithDivWidth > tdWidth ? setSpanWidth(true) : setSpanWidth(false)
            } else {
                const tdElement = ref.current;
                const tdElementCss = getComputedStyle(tdElement);
                const tdWidth = tdElement.offsetWidth - (parseFloat(tdElementCss.paddingLeft) + parseFloat(tdElementCss.paddingRight))
                
                const span = tdElement.querySelector('span');
                const spanWidth = span?.scrollWidth;
                spanWidth > tdWidth ? setSpanWidth(true) : setSpanWidth(false)
            }
           ;
        }
    };

    const setTitle = (): string => {
        if (spanWidth) {
            return item.displayName;
        } else if (searching) {
            return 'Нажмите для поиска';
        } else {
            return '';
        }
    }

    const getComponent = (): JSX.Element => {
        if (item.propertyType === 'string') {
            return (
                <TextInput
                style={{ height: pluginSettings.rowHeight - 10 }}
                autoFocus
                value={searchText}
                onChange={(value) => {
                    handleSearchChange(value);
                }}
                onEndChange={() => {
                    if (searchText.length >= MIN_SEARCH_LENGTH || searchText.length === 0) {
                        setShowSearchInput(false);
                    }
                }}
                onKeyDown={(e) => {
                    if (e.code === 'Enter') {
                        if (searchText.length >= MIN_SEARCH_LENGTH || searchText.length === 0) {
                            setShowSearchInput(false);
                        }
                    }
                }}
            />
        )
        }

        if (item.propertyType === 'date' || item.propertyType === 'datetime') {
            return (
                <DateInput
                    inputId={item.propertyName}
                    onBlur={(value) => {
                        if (value?.isValid === false) {
                            setSearchText('')
                            onSearch?.('', item);
                            setShowSearchInput(false)
                            return
                        }

                        if (value?.isValid === undefined) {
                            return
                        }

                        setSearchText(value?.toFormat("yyyyMMdd"));
                        onSearch?.(value?.toFormat("yyyyMMdd"), item);
                        setShowSearchInput(false)
                    }}
                />
            )
        }
        
        if (item.propertyType === 'decimal' || item.propertyType === 'number') {
            return (
                <DecimalInputV2 
                    inputId={item.propertyName}
                    autoFocus
                    value={searchText}
                    onChange={(value) => {
                        setSearchText(value.toString());
                        onSearch?.(value.toString(), item);
                    }}
                    onBlur={() => setShowSearchInput(false)}
                    onEndChange={() => setShowSearchInput(false)}
                />
            )
        }

        if (item.propertyType === 'boolean') {
            return (
                <CheckBox
                    id={item.propertyName}
                    defaultChecked={boolean}
                    onBlur={()=> setShowSearchInput(false)}
                    onChanged={(checked) => {
                        setSearchText(String(checked))
                        onSearch?.(String(checked), item)
                        setBoolean(checked)
                    }}
                />
            )
        }

        return <></>
    }

    return (
        <td
            ref={TableHeaderRef}
            onMouseEnter={() => actionForRef(TableHeaderRef)}
            onMouseDown={handleCellMouseDown}
            key={index}
            draggable={draggable}
            className={`${cls.tD} ${isResizing ? 'resizing' : ''}`}
            data-order-number={item.order}
            data-property-name={item.propertyName}
            data-filter-selected={filtered}
            style={{
                width: currentWidth,
                minWidth: currentWidth,
                background: searchText !== '' ? '#40a9ff82' : undefined,
                position: 'relative',
                cursor: isResizing ? 'col-resize' : 'default'
            }}
            onDragStart={(e) => {
                if (isResizing) {
                    e.preventDefault();
                    return;
                }
                e.dataTransfer.dropEffect = "move";
                e.dataTransfer.setData("application/json", JSON.stringify(item));
            }}
            onDragOver={(e) => {
                e.preventDefault();
                return false;
            }}
            onDrop={(e) => {
                e.preventDefault();
                const draggedColumn = JSON.parse(e.dataTransfer.getData("application/json")) as IGridColumn;
                const targetColumnOrder = Number((e.currentTarget as HTMLElement).getAttribute("data-order-number"));
                if (draggedColumn.order === targetColumnOrder) return;
                const updatedColumns = [...pluginSettings.columns];
                const draggedIndex = updatedColumns.findIndex(x => x.order === draggedColumn.order);
                const targetIndex = updatedColumns.findIndex(x => x.order === targetColumnOrder);
                const [removedColumn] = updatedColumns.splice(draggedIndex, 1);
                const insertPosition = targetIndex;
                updatedColumns.splice(insertPosition, 0, removedColumn);
                updatedColumns.forEach((column, index) => {
                    column.order = index;
                });
                onReorderEnd?.();
                setGridColumnsToSave({
                    columns: updatedColumns,
                    save: true
                });
            }}
        >
            {!showSearchInput && (
                <div
                    className={styles.resizeHandle}
                    style={{
                        opacity: isHoveringResize || isResizing ? 1 : 0.3,
                        backgroundColor: isResizing ? '#6c757d' : (isHoveringResize ? '#adb5bd' : '#ced4da'),
                    }}
                    onMouseEnter={handleResizeHandleMouseEnter}
                    onMouseLeave={handleResizeHandleMouseLeave}
                    onMouseDown={handleResizeHandleMouseDown}
                />
            )}
            {!showSearchInput ?
                <>
                    <Tooltip
                        className={cls.tooltip}
                        title={setTitle()}
                        placement="top"
                        autoAdjustOverflow={false}
                        overlayStyle={{ maxWidth: 300 }}
                        getPopupContainer={() => document.body}
                    >
                        {searching ?
                            <span 
                                style={{cursor: 'pointer'}}
                                onClick={() => setShowSearchInput(true)}
                            >
                                {item.displayName}
                            </span>
                        :
                            <>
                                {item.displayName}
                            </>
                        }
                    </Tooltip>
                    {sorting && (
                        <div draggable={false} className={classNames(cls.sortingDiv)}>
                            <div
                                draggable={false}
                                className={cls.sortingDivButton}
                                onClick={() => {
                                    onSort(item);
                                    if(onPageNumberChange && setPageNumber && filter && setLoadingStatus) {
                                        onPageNumberChange(1);
                                        setPageNumber({
                                            pageNumber: filter.pageNumber,
                                            prevPageNumber: filter.pageNumber,
                                        });
                                        setLoadingStatus(LoadingStatus.InProcess);
                                    }
                                }}
                            >
                                {renderSortIcon(item)}
                            </div>
                            {
                                (searching && !showSearchInput && searchText !== '') && (
                                    <div
                                        className={cls.searchDropDivButton}
                                    >
                                        <BaseIconButton
                                            sizeVariant={'normal'}
                                            onClick={() => {
                                                setSearchText('');
                                                onSearch?.('', item);
                                            }}
                                        >
                                            <MultiplyIcon />
                                        </BaseIconButton>
                                    </div>
                                )
                            }
                            {filtered && (
                                <div draggable={false} className={cls.filteredDiv} onClick={() => onFilterDelete(item)}>
                                    {" "}
                                    <MultiplyIcon />{" "}
                                </div>
                            )}
                        </div>
                    )}
                </>
                    :
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    {getComponent()}
                </div>
            }
        </td>
    );
};
